import { ChangeDetectorRef, Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { format } from 'date-fns';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, takeUntil } from 'rxjs';
import { DYNAMIC_REPORT_HTTP_LOADER, DynamicReportHttpLoader } from '../dynamic-report-http-loader.service';
import { PreviewDynamicReportDto } from '../dynamic-report-general.dto';
import { DynamicReportGeneralModel } from '../dynamic-report-general.model';
import { ExceljsCell, ExceljsCol, ExceljsNormalizedCell, ExceljsRow, ExceljsValueType, ExceljsWorkBook, ExceljsWorkBookMeta, ExceljsWorkSheet } from '@shared/components/exceljs-viewer';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ExceljsVirtualScrollControllerDirective } from 'libs/shared/components/exceljs-viewer/src/lib/exceljs-virtual-scroll-controller.directive';
import { cloneDeep, defaults } from 'lodash-es';
import { ToastService } from '@shared/components/toast';
import { DynamicReportType } from '../dynamic-report-general.interface';

@Component({
    selector: 'red-dynamic-report-general-preview',
    templateUrl: './dynamic-report-general-preview.component.html',
    styleUrls: ['./dynamic-report-general-preview.component.scss']
})
export class DynamicReportGeneralPreviewComponent implements OnInit {
    private _unsubscribeAll: Subject<void> = new Subject<void>();
    @Input() data!: DynamicReportGeneralModel;
    @Input() reportType: DynamicReportType | undefined = DynamicReportType.project;

    worksheet?: ExceljsWorkSheet;
    meta!: ExceljsWorkBookMeta;


    isLoading = false;
    columns: { columnDef: string; header: string; cell: (item: any, index?: number) => any; templateRef?: string }[] = [
        { columnDef: 'name', header: 'Report Name', cell: (item: any) => item.name },
        { columnDef: 'createdBy', header: 'Created By', cell: (item: any) => item.createdBy?.name },
        { columnDef: 'createdAt', header: 'Created On', cell: (item: any) => item?.createdAt ? format(new Date(item?.createdAt), 'dd MMM yyyy HH:mm a') : '' },
        { columnDef: 'updatedBy', header: 'Last Updated By', cell: (item: any) => `${item.createdBy?.name ?? '-'} ${item?.updatedAt ? ' on ' + format(new Date(item?.updatedAt), 'dd MMM yyyy HH:mm a') : ''}` },
    ];
    displayedColumns: string[] = [];
    rows: ExceljsRow[] = [];
    cols: ExceljsCol[] = [];
    merges: string[] = [];
    dataSource: ExceljsRow[] = [];
    dataHeader: ExceljsRow[] = [];
    matries = [];
    defaultColWidth?: number;
    defaultRowHeight: number = 40;
    defaultBackgroundColor = 'ffffff';
    exceljsValueType = ExceljsValueType;
    @ViewChild(CdkVirtualScrollViewport, { static: true })
    viewPort!: CdkVirtualScrollViewport;
    topHeader = 0;

    @ViewChild(ExceljsVirtualScrollControllerDirective) public scrollController?: ExceljsVirtualScrollControllerDirective;

    constructor(
        @Inject(DYNAMIC_REPORT_HTTP_LOADER) private _apiService: DynamicReportHttpLoader,

        protected _router: Router,
        protected _cdr: ChangeDetectorRef,
        protected _activatedRoute: ActivatedRoute,
        protected _formBuilder: UntypedFormBuilder,
        private _toastService: ToastService,

    ) {

    }
    ngOnInit(): void {
        this.isLoading = true;
        const filterReport = PreviewDynamicReportDto.fromJson({ ...this.data, reportType: this.reportType ?? this.data?.reportType });
        this._apiService.createPreview(0, filterReport).pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (data) => {
                    this.isLoading = false;
                    this.worksheet = data?.worksheets?.[0];
                    this.meta = data?.meta as ExceljsWorkBookMeta;
                    this.renderTable();
                },
                error: (error) => {
                    this.isLoading = false;
                    this._toastService.open({
                        type: 'danger',
                        title: 'Error',
                        messages: error.message ?? 'Something went wrong',
                    });
                }
            })
    }

    renderTable() {
        this.rows = this.worksheet?.rows || [];
        this.cols = this.worksheet?.cols || [];
        this.dataSource = [];
        this.merges = this.worksheet?.merges || [];
        this.defaultColWidth = this.worksheet?.properties.defaultColWidth;
        this.defaultRowHeight = 40;
        this.renderRows();
    }

    renderRows(): void {
        for (const row of this.rows) {
            if (row.number > 0) {
                const selectedRow = cloneDeep(row);
                let selectedIndex = 1;
                while (selectedIndex < row.min) {
                    const emptyCell = ExceljsNormalizedCell.createEmpty();
                    selectedRow.cells.unshift(emptyCell);
                    selectedIndex++;
                }
                this.renderRow(selectedRow);
            }
        }
    }
    renderRow(row: ExceljsRow): void {
        // for (const row of this.rows) {
        const rowCollection: ExceljsRow = ExceljsRow.fromJson({
            cells: row.cells.filter(cell => !cell.master).map(cell => this.renderCell(cell, row, this.merges)),
        });
        this.dataSource.push(rowCollection);
        // }
    }
    renderCell(cell: ExceljsCell, row: ExceljsRow, merges: string[] = []) {
        const attrs = this.getCellAttrs(cell, row, merges);
        return defaults(cell, { attrs });
    }
    getCellAttrs(cell: ExceljsCell, row: ExceljsRow, merges: string[]) {
        const mergeItem = merges.find(item => {
            const [start, end] = item.split(':');
            return start === cell.address;
        });
        if (!mergeItem) {
            return {
                colspan: 1,
                rowspan: 1,
            };
        }
        const [addressStart, addressEnd] = mergeItem.split(':');
        const addressStartName = (addressStart.match(/[A-Z]/g) as RegExpMatchArray).join('');
        const addressStartPosition = Number(addressStart.split(addressStartName)[1]);
        const rowStart = this.rows[addressStartPosition - 1];
        const cellStartIndex = rowStart.cells.findIndex(cellItem => cellItem.address === addressStart) + rowStart.min;

        const addressEndName = (addressEnd.match(/[A-Z]/g) as RegExpMatchArray).join('');
        const addressEndPosition = Number(addressEnd.split(addressEndName)[1]);
        const rowEnd = this.rows[addressEndPosition - 1];
        const cellEndIndex = rowEnd.cells.findIndex(cellItem => cellItem.address === addressEnd) + rowEnd.min;
        return {
            colspan: cellEndIndex - cellStartIndex + 1,
            rowspan: addressEndPosition - addressStartPosition + 1,
        };
    }
} 